/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable-next-line react/jsx-wrap-multilines */
import React, { FC, useRef, useState } from 'react';
import Modal from '@salesforce/design-system-react/components/modal';
import Button from '@salesforce/design-system-react/components/button';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import SignaturePad from 'react-signature-canvas';
import './index.scss';

interface Props {
  open: boolean;
  handleToggleModal: () => void;
  handleSaveSignature: (url: string) => void;
  existingSignature?: string;
  onClearSignature?: () => void;
}

const SignatureCanvas: FC<Props> = ({
  open,
  handleToggleModal,
  handleSaveSignature,
  existingSignature,
  onClearSignature,
}) => {
  const SIGNATURE_CANVAS_WIDTH = 600;
  const SIGNATURE_CANVAS_HEIGHT = 250;
  const sigCanvas = useRef<SignaturePad>(null);
  const [dataUrl, setDataUrl] = useState('');
  const [showCanvas, setShowCanvas] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingClear, setPendingClear] = useState(false); // Track if user wants to replace signature

  const hasExistingSignature = existingSignature && !showCanvas;

  const Clear = (): void => {
    if (sigCanvas.current) {
      setDataUrl('');
      sigCanvas.current.clear();
    }
  };

  const ClearExistingSignature = (): void => {
    setShowConfirmation(true);
  };

  const handleConfirmClear = (): void => {
    // The signature will only be replaced when the user saves a new one
    setPendingClear(true);
    setShowCanvas(true);
    Clear();
    setShowConfirmation(false);
  };

  const handleCancelClear = (): void => {
    setShowConfirmation(false);
  };

  const onEnd = (): void => {
    if (sigCanvas.current) {
      // Keep a fixed output size so all saved signatures have the same dimensions.
      const url = sigCanvas.current.getCanvas().toDataURL('image/png');
      setDataUrl(url);
    }
  };
  const Save = (): void => {
    // when saving a new signature, clear the old one if it was pending
    if (pendingClear && onClearSignature) {
      onClearSignature();
    }
    handleSaveSignature(dataUrl);
    Clear();
    setShowCanvas(false);
    setPendingClear(false);
    handleToggleModal();
  };
  const onCancel = (): void => {
    Clear();
    setShowCanvas(false);
    setPendingClear(false); // Reset pending clear when canceling
    handleToggleModal();
  };

  const footerButtons = hasExistingSignature
    ? [
        <Button label="Cancel" key="Cancel" onClick={onCancel} />,
        <Button
          label="Clear"
          key="Clear"
          onClick={ClearExistingSignature}
          className="yellow-btn"
          variant="brand"
        />,
      ]
    : [
        <Button label="Cancel" key="Cancel" onClick={onCancel} />,
        <Button label="Clear" key="Clear" onClick={Clear} disabled={!dataUrl} />,
        <Button
          className="yellow-btn"
          label="Save"
          onClick={Save}
          variant="brand"
          key="save"
          disabled={!dataUrl}
        />,
      ];

  return (
    <IconSettings iconPath="/icons">
      <Modal
        className="filter-modal surgery-sheet-pdf default-modal_header default-modal_footer"
        ariaHideApp={false}
        isOpen={open}
        onRequestClose={onCancel}
        footer={footerButtons}
        heading={[<div style={{ textAlign: 'start' }}>Signature</div>]}
      >
        <section className="slds-p-around_medium slds-theme_shade" style={{ overflowX: 'hidden' }}>
          <section style={{ background: 'white' }}>
            <div className="slds-p-around_large-1">
              {hasExistingSignature ? (
                <div className="signature-preview">
                  <img
                    src={existingSignature}
                    alt="Existing signature"
                    className="signatureCanvas"
                  />
                </div>
              ) : (
                <SignaturePad
                  ref={sigCanvas}
                  canvasProps={{
                    className: 'signatureCanvas',
                    width: SIGNATURE_CANVAS_WIDTH,
                    height: SIGNATURE_CANVAS_HEIGHT,
                  }}
                  onEnd={onEnd}
                />
              )}
            </div>
          </section>
        </section>
      </Modal>

      <Modal
        ariaHideApp={false}
        isOpen={showConfirmation}
        onRequestClose={handleCancelClear}
        heading="Confirm Clear Signature"
        footer={[
          <Button label="No" key="no" onClick={handleCancelClear} />,
          <Button
            label="Yes"
            key="yes"
            onClick={handleConfirmClear}
            variant="brand"
            className="yellow-btn"
          />,
        ]}
      >
        <section className="slds-p-around_medium">
          <p>This action will remove the signature. Do you want to continue?</p>
        </section>
      </Modal>
    </IconSettings>
  );
};

export default SignatureCanvas;
